import { computed, ref, getCurrentInstance } from 'vue';

export const TemplateType = Object.freeze({
  LIBRARY: 'library',
  VERIFIED: 'verified',
});

export function useTemplateManager() {
  const instance = getCurrentInstance();
  const { $globals } = instance.proxy;

  const loading = ref(false);
  const templates = ref(null);
  const templateLibraryFlagEnabled = ref(false);
  templateLibraryFlagEnabled.value = $globals.isFlagEnabled('template_library');

  const viewingTemplate = ref(false);

  const templateLibraryModal = ref(null);
  const viewTemplateModal = ref(null);
  const confirmModal = ref(null);

  const templateCategoryOptions = ref(null);
  const templateCreatedByOptions = ref(null);
  const templateTypeSelected = ref('');
  const templateScheduleSelected = ref('');

  const confirmBody = ref('');
  const confirmTitle = ref('');
  const confirmBtnText = ref('Yes');
  const cancelBtnText = ref('Cancel');
  const confirmAction = ref(null);
  const confirmCancel = ref(null);

  const canManageTemplates = computed(() => $globals.user().canManageTaskTemplates);

  async function setCategoryOptions(type) {
    if (type === TemplateType.LIBRARY) {
      templateCategoryOptions.value = await $globals.cachedData.getCompanyTaskCategories();
    } else if (type === TemplateType.VERIFIED) {
      templateCategoryOptions.value = await $globals.companyClient.getCompanyTaskCategoryDefaults();
    }
  }

  async function setCreatedByOptions(type = templateTypeSelected.value) {
    const createdMix = await $globals.templateLibraryClient.getCreatedByFilters(type);

    templateCreatedByOptions.value = createdMix.map((c, idx) => ({ ...c, index: idx }));
  }

  async function addTemplate() {
    await setCategoryOptions(TemplateType.LIBRARY);
    viewingTemplate.value = false;
    templateLibraryModal.value?.show();
  }

  async function viewTemplate({ template, selectMode }, type) {
    try {
      let response = null;
      if (type === TemplateType.LIBRARY) {
        response = await $globals.companyClient.getTaskTemplate(template.id);
      } else {
        response = await $globals.templateLibraryClient.getVerifiedTemplate(template.id);
      }
      viewTemplateModal.value?.show(response, type, selectMode);
      return response;
    } catch (ex) {
      $globals.showError('Failed to load template details. Please try again!', ex);
    }
  }

  async function editTemplate({ id, duplicate = false, locked = false }) {
    // don't allow edit for locked
    if (locked) {
      viewTemplate({ template: { id } }, TemplateType.LIBRARY);
      return;
    }

    setCategoryOptions(TemplateType.LIBRARY);
    try {
      const response = await $globals.companyClient.getTaskTemplate(id);

      if (duplicate) {
        response.id = 0;
        response.name = `Copy of ${response.name}`;
      }

      viewingTemplate.value = false;
      templateLibraryModal.value?.show(response, null, duplicate);
    } catch (ex) {
      $globals.showError('Failed to load template details. Please try again!', ex);
    }
  }

  function showConfirmModal(itemId, data, action, options = false, customField = null, onCancel = null) {
    confirmBtnText.value = data.btnText || 'Yes';
    cancelBtnText.value = data.cancelText || 'Cancel';
    confirmTitle.value = data.title || '';
    confirmBody.value = data.body || '';
    confirmCancel.value = onCancel;

    confirmAction.value = async (whenDone) => {
      await action(whenDone, null, () => {
        confirmBtnText.value = 'Yes';
        confirmModal.value.hide();
      });
    };

    confirmModal.value.show(options, customField);
  }

  return {
    loading,
    templates,
    templateLibraryFlagEnabled,
    viewingTemplate,
    templateCategoryOptions,
    templateCreatedByOptions,
    templateTypeSelected,
    templateScheduleSelected,
    confirmBody,
    confirmTitle,
    confirmBtnText,
    cancelBtnText,
    confirmAction,
    confirmCancel,
    setCategoryOptions,
    setCreatedByOptions,
    addTemplate,
    viewTemplate,
    editTemplate,
    showConfirmModal,
    templateLibraryModal,
    viewTemplateModal,
    confirmModal,
    canManageTemplates,
  };
}
